// FRONT-1469: the OpenAI Realtime protocol allows exactly one active
// `response` at a time — a second `response.create` sent while one is still
// running is rejected with "Conversation already has an active response in
// progress". `use-voice-call.ts` used to send `response.create`
// unconditionally at the end of every tool call, which collided in two real
// situations:
//
//   1. Fast tool — the tool's HTTP round-trip finishes and
//      `handleToolCall` sends `response.create` while the response that
//      REQUESTED the tool call is still open (its own `response.done`
//      hasn't arrived yet).
//   2. Parallel tools — one model turn asks for several tools in the same
//      response. Each tool call independently finishes and tries to open a
//      new response; only the first can succeed.
//
// This module is the single place that decides what goes down the data
// channel after a tool result, and when a new reply may be requested. It is
// deliberately framework-free (no WebRTC, no React) so it can be unit-tested
// under the plain node vitest runner this repo's frontend tests run under —
// see `frontend/package.json`'s `test` script (plain `vitest run`, no jsdom
// environment configured) and the absence of any testing-library dependency
// in `frontend/package.json`. `use-voice-call.ts` forwards tool lifecycle
// into `toolCallDispatched`/`toolResultReady` and every inbound event into
// `observe`, then sends whatever frames come back — but it is not a pure
// pipe: it still owns two decisions the gate has no visibility into (exactly
// where in the tool-call flow to register a call, and what to do when the
// data channel isn't open). See the call sites in `use-voice-call.ts` for
// both.

/** One realtime protocol frame, ready to be JSON.stringify'd onto the data channel. */
export type VoiceFrame = Record<string, unknown>;

/** A response we never saw finish is treated as finished after this long, so a
 *  dropped `response.done` can't mute the rest of the call. Only trips when
 *  a response is genuinely silent for this long — see `isTrafficFromActiveResponse`
 *  and the refresh in `observe`. */
export const STALE_RESPONSE_MS = 90_000;

// Loosely typed inbound event — mirrors `voice-event-router.ts`'s own
// `RealtimeEvent`, since this reads the same wire shapes (in particular the
// two `error` payload shapes that router's `error` case already handles).
// Two different id shapes matter here: `response.created`/`response.done`
// carry a nested `response.id`, while the various streaming events (deltas,
// `response.function_call_arguments.done`, ...) instead carry a flat
// `response_id`. `eventResponseId` reads both; its two consumers
// (`mayClearActiveResponse`, `isTrafficFromActiveResponse`) treat a MISSING
// id in deliberately opposite ways — see each for why.
interface RealtimeEvent {
  type?: string;
  response?: { id?: string };
  response_id?: string;
  error?: { message?: string } | string;
  message?: string;
}

/** FRONT-254: a user who has been "speaking" this long without a
 *  `speech_stopped` is assumed to have lost that event, so a missing stop
 *  can't hold every reply for the rest of the call. Generous on purpose: a
 *  person dictating can talk for a minute, and talking over them is the bug
 *  this guard exists for. */
export const USER_SPEECH_STALE_MS = STALE_RESPONSE_MS;

/** FRONT-254: after the user stops, server VAD creates the reply to their
 *  turn within a fraction of a second. If none has appeared after this long,
 *  the turn is taken to have ended without one, and a reply held for it is
 *  released rather than lost. */
export const USER_TURN_REPLY_GRACE_MS = 5_000;

const ACTIVE_RESPONSE_ERROR_TEXT = 'active response in progress';

// Sentinel `activeResponseId` values. Distinct from any real OpenAI response
// id (always shaped like `resp_...`), so an `===` compare can never
// accidentally match a real id.
//
// REQUESTED: set the instant the gate itself emits a `response.create`, for
// the window before the server's own `response.created` echoes back with the
// real id (hole b — without this, a tool result landing in that window
// would fire a second create).
const RESPONSE_REQUESTED = '__gate_requested__';
// UNKNOWN: a response is known to be active but we don't have its real id —
// either `response.created` itself omitted one, or the error self-heal
// inferred one is running without ever seeing it created.
const RESPONSE_UNKNOWN = '__gate_unknown__';
// Prefix for a per-call "solo" bucket key (see `bucketFor`). Never a value
// of `activeResponseId` — only ever a value inside `outstanding`, and always
// unique to one call id, so it can never be shared the way a fixed sentinel
// bucket could.
const RESPONSE_SOLO_PREFIX = '__gate_solo_';

export class VoiceTurnGate {
  private activeResponseId: string | null = null;
  private activeSince = 0;
  // Keyed by call id, valued by a bucket key from `bucketFor` — a real
  // response id when we know one, or a bucket unique to that single call
  // otherwise. Coalescing only waits on tool calls that share the SAME
  // bucket, so a stuck call from an earlier turn — or an unrelated call
  // with no response id of its own — can never block a turn other than its
  // own.
  //
  // Deliberately no timeout on an entry. That guarantee — a stuck entry
  // only ever loses its OWN turn's reply, e.g. a tier-3 delegated call
  // blocked on the approval gate's five-minute `APPROVAL_TIMEOUT_MS`
  // (src/autonomy/gate.ts) — holds ONLY because every bucket key is either
  // a real response id (shared exclusively by tool calls that genuinely
  // belong to that one response) or unique to a single call (`bucketFor`'s
  // solo path). That invariant is what makes an eviction bound unnecessary;
  // if a future change ever reintroduces a shared fallback bucket, a stuck
  // entry can block unrelated calls again and this reasoning no longer
  // holds. As long as the invariant holds, don't add a bound — it's one
  // more number to get wrong.
  private readonly outstanding = new Map<string, string>();
  // FRONT-254: buckets that received at least one result that wants a reply.
  // A turn whose every result was silent (a successful screen move, where a
  // reply only makes EDDY say the same thing twice — measured) ends without
  // a `response.create`; one loud sibling still gets the turn its reply.
  // Cleared when the bucket's last result lands.
  private readonly wantsReply = new Set<string>();
  private pendingCreate = false;
  // Which bucket `pendingCreate` is queued on behalf of. Read by
  // `flushPendingCreate` to decide whether a sibling tool call from that
  // same turn is still outstanding, and preserved as-is by the error
  // self-heal when re-queuing a rejected create.
  private pendingForResponseId: string | undefined;
  // One-shot: true from the moment the gate emits a `response.create` until
  // either a `response.done` arrives or the error self-heal consumes it.
  // Exists ONLY to drive the self-heal condition below — see `markRequested`
  // for why `activeResponseId === RESPONSE_REQUESTED` alone isn't enough.
  // Nothing outside `markRequested`, the `response.done` branch, and the
  // self-heal itself may set or clear this.
  private awaitingCreateAck = false;
  // FRONT-254 turn-taking. The gate used to know only about responses, so a
  // tool result that landed while the PERSON was talking fired a
  // `response.create` straight over them (server VAD only interrupts a
  // response that is already running when speech starts; one created after
  // that plays on top of the speaker), and a reply queued before a barge-in
  // was replayed the instant the interrupted response's `response.done`
  // arrived, while the person was still mid-sentence. Both ended with two
  // replies: ours, then the one server VAD creates for the person's turn.
  //
  // The person's turn runs from `speech_started` to the `response.created`
  // server VAD makes for it. Two phases, because they differ in what that
  // response will have seen:
  //  - SPEAKING (`userSpeaking`): any output sent now is in the conversation
  //    before the audio is committed, so the turn's own reply covers it. Its
  //    reply is HELD (`heldReply`) and dropped once that response appears.
  //  - AFTER THE STOP (`awaitingTurnResponse`): server VAD commits and
  //    creates its response at the stop, so output sent now arrives too late
  //    to be in it. Its reply is queued (`pendingCreate`) for after.
  private userSpeaking = false;
  private userSpeechSince = 0;
  private awaitingTurnResponse = false;
  private turnStoppedAt = 0;
  private heldReply = false;

  /** Record that a tool call is in flight, scoped to the model turn that
   *  requested it — see `bucketFor` for the exact scoping rule, including
   *  the no-response-id fallback. Synchronous and called before the tool's
   *  fetch is even issued, so parallel tool calls all register before any
   *  of them can resolve — see the call site in `handleToolCall`. */
  toolCallDispatched(callId: string, responseId: string | undefined, now: number): void {
    this.outstanding.set(callId, this.bucketFor(callId, responseId, now));
  }

  /** A tool call's result is ready to go back to the model. Returns the
   *  frames to send, in order. Several tool results from the SAME model
   *  turn coalesce into exactly one `response.create` — the last one to
   *  finish (for that turn) is the one that (maybe) requests the reply. */
  toolResultReady(callId: string, output: string, now: number, opts?: { silent?: boolean }): VoiceFrame[] {
    const responseId = this.outstanding.get(callId);
    const wasOutstanding = this.outstanding.delete(callId);
    if (responseId !== undefined && !opts?.silent) this.wantsReply.add(responseId);

    const frames: VoiceFrame[] = [
      {
        type: 'conversation.item.create',
        item: {
          type: 'function_call_output',
          call_id: callId,
          output,
        },
      },
    ];

    // A result for a call id we never dispatched (or already resolved)
    // still gets forwarded, but must never open a spurious turn. On the
    // real path this never happens — `handleToolCall` returns before
    // `toolCallDispatched` if the data channel isn't open — but a stray
    // duplicate must not go on to request a reply.
    if (!wasOutstanding || responseId === undefined) return frames;

    // Another tool call from the SAME model turn is still outstanding —
    // wait for the last one before asking for a reply.
    if (this.hasOutstandingForResponse(responseId)) return frames;

    // FRONT-254: every result of this turn was silent — no reply. Never true
    // for a caller that passes no options, so every pre-FRONT-254 result
    // still asks for its reply exactly as before.
    const reply = this.wantsReply.delete(responseId);
    if (!reply) return frames;

    return this.requestReply(responseId, now, frames);
  }

  /** Call for EVERY inbound realtime event. Returns frames to send (usually
   *  none). */
  observe(raw: unknown, now: number): VoiceFrame[] {
    if (!raw || typeof raw !== 'object') return [];
    const e = raw as RealtimeEvent;
    const type = e.type ?? '';

    // Refresh the active-response clock on `response.*` traffic that is
    // POSITIVELY from the response we're tracking — not just at
    // `response.created`. Output-audio deltas and friends stream
    // continuously while a response is genuinely working, so staleness has
    // to measure SILENCE FROM THAT RESPONSE: not its total lifetime, and
    // not unrelated traffic. See `isTrafficFromActiveResponse` for why this
    // predicate demands a positive id match while its sibling
    // `mayClearActiveResponse` deliberately does not.
    if (type.startsWith('response.') && this.isTrafficFromActiveResponse(e)) {
      this.activeSince = now;
    }

    if (type === 'input_audio_buffer.speech_started') {
      this.userSpeaking = true;
      this.userSpeechSince = now;
      this.awaitingTurnResponse = false;
      // Barge-in: a reply queued before the person started talking is never
      // replayed over them. Its output is already in the conversation, so
      // the reply to what they are saying now covers it.
      if (this.pendingCreate) {
        this.pendingCreate = false;
        this.heldReply = true;
      }
      return [];
    }

    if (type === 'input_audio_buffer.speech_stopped') {
      this.userSpeaking = false;
      this.awaitingTurnResponse = true;
      this.turnStoppedAt = now;
      return [];
    }

    if (type === 'response.created') {
      // Our create crossed the person starting to talk: it left before their
      // `speech_started` reached us, and server VAD's interrupt only cancels a
      // response already running at that moment, so this one would play on
      // top of them. Server VAD never creates a response mid-speech, so a
      // response created while they talk, with our create unacknowledged, is
      // ours. Cut it; the turn's own reply covers its output.
      const crossed = this.awaitingCreateAck && this.isUserSpeaking(now);
      this.activeResponseId = e.response?.id ?? RESPONSE_UNKNOWN;
      this.activeSince = now;
      if (crossed) {
        this.awaitingCreateAck = false;
        this.heldReply = true;
        return [{ type: 'response.cancel' }, { type: 'output_audio_buffer.clear' }];
      }
      if (this.awaitingTurnResponse) {
        // The reply to the person's turn. It saw every output sent while
        // they were talking, so those held replies are answered. It also
        // answers a create of ours the server rejected because of it, so
        // that rejection must not be retried into a second reply.
        this.awaitingTurnResponse = false;
        this.heldReply = false;
        this.awaitingCreateAck = false;
      }
      return [];
    }

    if (type === 'response.done') {
      // Only clear when this event can actually be trusted to be FOR the
      // response we think is active. A real, DIFFERENT id must not clear:
      // that's a late/stale done for an earlier response landing after a
      // newer one has already started, and clearing on it would fire a
      // create straight into the still-active newer response.
      //
      // Known gap: while `activeResponseId` is a sentinel (no real id to
      // compare against), an UNRELATED `response.done` still "belongs" by
      // this rule, because a sentinel can't be checked against anything —
      // so the id-match protection above only covers a REAL tracked id, not
      // a sentinel one. Bounded here, not silent: it just clears early, and
      // the next `requestReply`/`flushPendingCreate` re-evaluates whether a
      // reply is still owed, so the worst case at THIS call site is one
      // avoidable request that self-heals — never a stuck call. Note that
      // reasoning is specific to clearing; the same predicate at the
      // `activeSince` refresh site would have the opposite worst case,
      // which is why the refresh carries its own `!isSentinel` guard.
      if (this.mayClearActiveResponse(e)) {
        this.activeResponseId = null;
      }
      // The ack window is closed here rather than on `response.created`,
      // and that choice owns a known trade the protocol makes unavoidable:
      // the error carries no correlation id, so a single flag cannot tell
      // "the rejection of OUR create" from "someone else's error", and the
      // two candidate close points each leak one case.
      //   Closing on `created` (the earlier cut) loses the CROSSING race —
      //   the server VAD-creates a response and rejects our create, its
      //   `created` lands before the rejection, and the retry is dropped.
      //   That is the most likely way to hit this error at all.
      //   Closing on `done` (here) instead leaves the window open for the
      //   life of the response our create started, so a stray in-window
      //   error can queue one reply nobody asked for.
      // The second is the thinner trigger — this gate is the only thing
      // that ever sends a create, and it cannot send one while a response
      // is active — so `done` is the deliberate pick. Not airtight; a
      // `response.done` for a response that finished BEFORE our create
      // reached the server still closes the window early and loses that
      // retry (bounded: the response that rejected us can see the
      // function_call_output already in the conversation).
      this.awaitingCreateAck = false;
      return this.flushPendingCreate(now);
    }

    if (type === 'error') {
      // Lowercased before matching: the live server message is lowercase,
      // but a wording or casing change upstream must not silently turn the
      // self-heal off — failing to recognise our own rejection costs the
      // user a reply, and nothing would report it.
      const msg = (
        typeof e.error === 'string' ? e.error : (e.error?.message ?? e.message ?? '')
      ).toLowerCase();
      // Self-heal keys off the dedicated `awaitingCreateAck` flag, NOT
      // `activeResponseId === RESPONSE_REQUESTED`. The latter breaks on the
      // crossing race: the server's own VAD-triggered `response.created`
      // for a REAL, unrelated response can land before the rejection of OUR
      // create, overwriting `activeResponseId` away from the sentinel — at
      // which point that condition no longer recognises the rejection as
      // ours and silently drops it, losing the reply. `awaitingCreateAck`
      // survives that overwrite: it's set only by `markRequested` and
      // cleared only here-on-heal or by the next `response.done`, so it
      // tracks "still waiting to learn whether OUR create lands" regardless
      // of what `activeResponseId` gets overwritten to in between.
      if (msg.includes(ACTIVE_RESPONSE_ERROR_TEXT) && this.awaitingCreateAck) {
        this.awaitingCreateAck = false;
        this.activeResponseId = RESPONSE_UNKNOWN;
        this.activeSince = now;
        // FRONT-254: rejected while the person is talking. The reply to
        // their turn covers it, same as any other reply held for them.
        if (this.isUserSpeaking(now)) this.heldReply = true;
        else this.pendingCreate = true;
        // pendingForResponseId is left untouched — we're retrying the exact
        // same batch, not starting a new one.
      }
      return [];
    }

    // Any other event: the unstick path for a dropped `response.done` — a
    // live call emits events constantly, so checking here means a lost
    // terminal event can't mute the call for longer than STALE_RESPONSE_MS.
    return this.flushPendingCreate(now);
  }

  /** Whether an inbound `response.*` event can be trusted to be FOR the
   *  response we currently believe is active — see the two known-gap
   *  comments at its call sites for what this does and doesn't cover. */
  /** The event's own response id, whichever shape carries it. */
  private eventResponseId(e: RealtimeEvent): string | undefined {
    return e.response?.id ?? e.response_id;
  }

  /** MAY this event clear the response we're tracking? Permissive on
   *  purpose: a sentinel has no real id to compare against, and a terminal
   *  event that carries no id at all is still the only ending we will get.
   *  A real, DIFFERENT id must not clear — that's a late terminal event for
   *  an earlier response landing after a newer one started. */
  private mayClearActiveResponse(e: RealtimeEvent): boolean {
    const id = this.eventResponseId(e);
    return (
      this.isSentinel(this.activeResponseId) ||
      id === undefined ||
      id === this.activeResponseId
    );
  }

  /** IS this event traffic FROM the response we're tracking? Strict on
   *  purpose, and NOT interchangeable with `mayClearActiveResponse` — this
   *  is the pair that used to be one predicate with two incompatible
   *  meanings, patched inline at each call site, which is exactly the drift
   *  shape this module exists to end.
   *
   *  Two rules, both of which cost a live call its voice when relaxed:
   *  - A SENTINEL is never refreshed. A sentinel matches everything, so
   *    unrelated traffic would keep it fresh forever and a queued reply
   *    would sit unsent past any window. A sentinel is meant to be
   *    short-lived (REQUESTED lasts until the server echoes
   *    `response.created`), so staling out on wall time IS its valve.
   *  - An event with NO id does not count as traffic. Being permissive here
   *    was the same bug one level down: a tracked id whose own
   *    `response.done` was lost had its clock held open indefinitely by any
   *    id-less `response.*` event, and the queued reply never fired. Real
   *    streaming events carry a flat `response_id`, so demanding it costs
   *    nothing on the wire — and it flips the failure direction from "mute
   *    forever" to "one early create", which the self-heal already
   *    recovers. Prefer the recoverable failure. */
  private isTrafficFromActiveResponse(e: RealtimeEvent): boolean {
    if (this.activeResponseId === null) return false;
    if (this.isSentinel(this.activeResponseId)) return false;
    return this.eventResponseId(e) === this.activeResponseId;
  }

  private isSentinel(id: string | null): boolean {
    return id === RESPONSE_REQUESTED || id === RESPONSE_UNKNOWN;
  }

  /** The coalescing bucket a dispatched call belongs to. A real
   *  `response_id` wins outright. Missing that, fall back to the response
   *  we already know is active — ONLY if it's a real id, not a sentinel and
   *  not null — since a tool call can only ever be emitted BY a running
   *  response, so it's the same turn by construction. With neither, give
   *  the call a bucket unique to itself: never a shared fallback (that was
   *  the bug — two calls from genuinely different, unrelated turns sharing
   *  one bucket and blocking each other), so a solo-bucketed call can
   *  never block, or be blocked by, anything else. It degrades to one
   *  `response.create` per tool result for that call only — the gating
   *  half of the design (`requestReply`/`isResponseActive`) still
   *  serializes that behind whatever response is actually active, so the
   *  FRONT-1469 protocol error still cannot occur — but be clear about the
   *  user-visible cost: two solo-bucketed calls that were genuinely
   *  siblings of ONE turn produce two replies instead of one, i.e. Eddy
   *  answers twice. Reaching that needs a missing `response_id` AND no
   *  known-active response, so it is the last-resort path, not the normal
   *  one. Answering twice is the right trade against not answering. */
  private bucketFor(callId: string, responseId: string | undefined, now: number): string {
    if (responseId !== undefined) return responseId;
    // `isResponseActive` (not a bare null/sentinel test) on purpose: a real
    // id that is already past its stale window is NOT the turn we're in —
    // it is a leftover whose `response.done` we never saw. Bucketing into
    // it would break the very invariant the no-eviction decision rests on
    // ("a real id is shared exclusively by calls that genuinely belong to
    // that response") and could park a call behind a stuck sibling from a
    // dead turn, with no valve — `flushPendingCreate` reaches its sibling
    // check before staleness is ever consulted. Falling through to a solo
    // bucket is always safe.
    if (this.isResponseActive(now) && !this.isSentinel(this.activeResponseId)) {
      return this.activeResponseId as string;
    }
    return `${RESPONSE_SOLO_PREFIX}${callId}`;
  }

  /** True when some OTHER outstanding tool call belongs to the given
   *  bucket. */
  private hasOutstandingForResponse(responseId: string): boolean {
    for (const value of this.outstanding.values()) {
      if (value === responseId) return true;
    }
    return false;
  }

  /** Either fire a `response.create` for this turn now, or queue it — shared
   *  by `toolResultReady` and `flushPendingCreate`. */
  private requestReply(responseId: string, now: number, frames: VoiceFrame[]): VoiceFrame[] {
    this.pendingForResponseId = responseId;
    // FRONT-254: never over the person. See `userSpeaking` for why a result
    // landing while they talk is held, and one landing after they stop is
    // queued behind the reply to their turn.
    if (this.isUserSpeaking(now)) {
      this.pendingCreate = false;
      this.heldReply = true;
      return frames;
    }
    if (this.isUserTurnOpen(now) || this.isResponseActive(now)) {
      // A response is still running (or unknown-but-recent) — queue the
      // request instead of sending it now; `observe` fires it once that
      // response actually finishes.
      this.pendingCreate = true;
      return frames;
    }
    this.pendingCreate = false;
    this.markRequested(now);
    frames.push({ type: 'response.create' });
    return frames;
  }

  /** No response is active when we never saw one start, or the one we saw
   *  start is old enough to assume its `response.done` was dropped. */
  private isResponseActive(now: number): boolean {
    if (this.activeResponseId === null) return false;
    if (now - this.activeSince >= STALE_RESPONSE_MS) {
      this.activeResponseId = null;
      return false;
    }
    return true;
  }

  private flushPendingCreate(now: number): VoiceFrame[] {
    // FRONT-254: asked first, because a turn that ended without a reply of
    // its own is what turns a held reply back into a pending one.
    if (this.isUserTurnOpen(now)) return [];
    if (!this.pendingCreate) return [];
    if (this.pendingForResponseId !== undefined && this.hasOutstandingForResponse(this.pendingForResponseId)) {
      // A sibling tool call from the SAME turn is still outstanding — wait.
      //
      // Known limit, corrected after review actually ran it: if that
      // sibling was registered AFTER this turn's create was queued, no
      // valve fires HERE — this check sits before the staleness check
      // below, so staleness can never reach it. But the create is not
      // lost: when the late sibling's own result arrives,
      // `toolResultReady` reaches `requestReply` again and emits it. The
      // reply is only lost if that sibling NEVER resolves, which is the
      // ordinary stuck-call case documented on `outstanding` above. The
      // earlier wording here said "lost for good" and was simply wrong —
      // in a module whose comments ARE the contract, that is a defect.
      return [];
    }
    if (this.isResponseActive(now)) return [];
    this.pendingCreate = false;
    this.markRequested(now);
    return [{ type: 'response.create' }];
  }

  /** Is the person talking right now? A start with no stop for
   *  USER_SPEECH_STALE_MS is a lost `speech_stopped`, not a person still
   *  talking. */
  private isUserSpeaking(now: number): boolean {
    if (!this.userSpeaking) return false;
    if (now - this.userSpeechSince < USER_SPEECH_STALE_MS) return true;
    this.userSpeaking = false;
    this.releaseHeldReply();
    return false;
  }

  /** Is the person's turn still open: talking, or stopped and waiting for
   *  the reply server VAD makes for it? A stop followed by no reply within
   *  USER_TURN_REPLY_GRACE_MS ends the turn without one. */
  private isUserTurnOpen(now: number): boolean {
    if (this.isUserSpeaking(now)) return true;
    if (!this.awaitingTurnResponse) return false;
    if (now - this.turnStoppedAt < USER_TURN_REPLY_GRACE_MS) return true;
    this.awaitingTurnResponse = false;
    this.releaseHeldReply();
    return false;
  }

  /** The turn ended with no reply of its own to fold a held reply into, so
   *  it is owed after all: queue it rather than lose it. */
  private releaseHeldReply(): void {
    if (!this.heldReply) return;
    this.heldReply = false;
    this.pendingCreate = true;
  }

  /** Record that the gate itself just emitted a `response.create` — see the
   *  RESPONSE_REQUESTED doc above for why `activeResponseId` has to reflect
   *  this immediately, not only once `response.created` echoes back.
   *
   *  Known limit: this runs when a frame is EMITTED (returned to the
   *  caller), not when it is actually SENT over the wire. If `dc.send`
   *  throws for that frame (caught and logged in `use-voice-call.ts`), the
   *  gate still believes a response is active for up to STALE_RESPONSE_MS —
   *  a real `response.created`/`response.done` pair from the user's very
   *  next utterance clears it sooner than the stale timeout would. */
  private markRequested(now: number): void {
    this.activeResponseId = RESPONSE_REQUESTED;
    this.activeSince = now;
    this.awaitingCreateAck = true;
  }
}
